import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Send, X, Zap } from "lucide-react";
import axios from "axios";
import toast from 'react-hot-toast';
import HorizontalMatrixBackground from "@/components/HorizontalMatrixBackground";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

// Helper function to clean HTML whitespace and fix massive spacing
const cleanHtml = (html) => {
  if (!html) return html;
  // Remove excessive whitespace between tags
  let cleaned = html.replace(/>\s+</g, '><').trim();
  // Remove multiple consecutive <br> tags
  cleaned = cleaned.replace(/(<br\s*\/?>\s*){2,}/gi, '<br>');
  // Remove empty paragraphs
  cleaned = cleaned.replace(/<p>\s*<\/p>/gi, '');
  // Normalize spacing after closing tags
  cleaned = cleaned.replace(/<\/(p|li|h[1-6])>\s+/gi, '</$1>');
  return cleaned;
};

// Remove old token function - now using global Firebase auth via axios interceptor

const FloatingChatWidget = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  
  // Get current user ID for chat history isolation
  const getCurrentUserId = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.id || user.firebase_uid || null;
      }
    } catch (e) {
      console.error('Failed to get user ID:', e);
    }
    return null;
  };

  // Load chat history from localStorage (user-specific) or use default welcome message
  const loadChatHistory = () => {
    const userId = getCurrentUserId();
    if (!userId) {
      // No user logged in, return welcome message
      return [
        {
          role: 'assistant',
          content: "Hello! I'm Lawra, your AI legal assistant. Ask me any legal question and I'll provide detailed, professional guidance. How can I help you today?"
        }
      ];
    }
    
    const saved = localStorage.getItem(`lawra_chat_history_${userId}`);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to load chat history:', e);
      }
    }
    return [
      {
        role: 'assistant',
        content: "Hello! I'm Lawra, your AI legal assistant. Ask me any legal question and I'll provide detailed, professional guidance. How can I help you today?"
      }
    ];
  };
  
  const [messages, setMessages] = useState(loadChatHistory());
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [aiStatus, setAiStatus] = useState('online'); // 'online', 'thinking', 'idle'
  const messagesEndRef = useRef(null);
  const chatWindowRef = useRef(null);
  const inputRef = useRef(null);
  const streamingMessageRef = useRef(null);
  const lastMessageRef = useRef(null);

  // Don't show on homepage or auth pages
  // Check if user is authenticated via localStorage (set by AuthProvider)
  const shouldShow = !['/'].includes(location.pathname) && 
                     !location.pathname.includes('/auth') && 
                     localStorage.getItem('user') !== null;

  // Listen for custom event to open chat
  useEffect(() => {
    const handleOpenChat = () => {
      setIsOpen(true);
    };
    
    window.addEventListener('openLawraChat', handleOpenChat);
    return () => window.removeEventListener('openLawraChat', handleOpenChat);
  }, []);

  // Detect user changes and reload chat history
  useEffect(() => {
    const currentUserId = getCurrentUserId();
    const storageHandler = () => {
      const newUserId = getCurrentUserId();
      // If user changed (logged out or switched accounts), reload chat history
      if (currentUserId !== newUserId) {
        setMessages(loadChatHistory());
      }
    };
    
    window.addEventListener('storage', storageHandler);
    return () => window.removeEventListener('storage', storageHandler);
  }, []);

  // Save chat history to localStorage whenever messages change (user-specific)
  useEffect(() => {
    if (messages.length > 0) {
      const userId = getCurrentUserId();
      if (userId) {
        localStorage.setItem(`lawra_chat_history_${userId}`, JSON.stringify(messages));
      }
    }
  }, [messages]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const scrollToMessage = (messageIndex) => {
    // Scroll to the top of the latest message instead of the bottom
    const chatWindow = chatWindowRef.current;
    if (!chatWindow) return;
    
    const messageElements = chatWindow.querySelectorAll('.lawra-message');
    if (messageElements && messageElements.length > 0) {
      const lastMessage = messageElements[messageElements.length - 1];
      if (lastMessage) {
        // Scroll so the message starts at the top of the viewport
        lastMessage.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  useEffect(() => {
    if (isOpen && messages.length > 0) {
      // Small delay to ensure DOM has updated
      setTimeout(() => scrollToMessage(messages.length - 1), 50);
      // Focus input when opened
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [messages, isOpen]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setAiStatus('thinking');

    // Create a temporary message ID to track streaming message
    const streamingMsgId = Date.now();

    try {
      // Get user data from localStorage (set by AuthProvider)
      const userDataStr = localStorage.getItem('user');
      const userData = userDataStr ? JSON.parse(userDataStr) : null;
      
      // Get Firebase ID token
      const { auth } = await import('./firebase');
      const firebaseUser = auth.currentUser;
      
      if (!firebaseUser) {
        toast.error('Please login to use the chat');
        setIsLoading(false);
        return;
      }
      
      const idToken = await firebaseUser.getIdToken();
      
      const requestBody = { 
        messages: [...messages, userMessage],
        user_context: {
          country: userData?.country || 'United Kingdom',
          jurisdiction: userData?.jurisdiction || 'UK Law',
          name: userData?.full_name || 'User',
          law_firm: userData?.law_firm || null,
          is_lawyer: true
        }
      };

      const response = await fetch(`${API}/ask-spencer-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Failed to connect to streaming endpoint');
      }

      // Add initial empty message
      setMessages(prev => [...prev, { role: 'assistant', content: '', id: streamingMsgId }]);
      setIsLoading(false);
      
      let accumulatedText = '';
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            if (data === '[DONE]') break;
            
            try {
              const parsed = JSON.parse(data);
              if (parsed.content) {
                accumulatedText += parsed.content;
                
                // Update message immediately
                setMessages(prev => 
                  prev.map(msg => 
                    msg.id === streamingMsgId 
                      ? { ...msg, content: accumulatedText }
                      : msg
                  )
                );
              }
            } catch (e) {
              console.error('Parse error:', e);
            }
          }
        }
      }

      setAiStatus('online');
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Error communicating with Lawra. Please try again.');
      setMessages(prev => prev.filter(msg => msg.id !== streamingMsgId));
      setInput(userMessage.content);
      setAiStatus('online');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!shouldShow) return null;

  return (
    <>
      {/* Floating Chat Button - Futuristic Design */}
      <button
        className={`lawra-chat-launcher ${isOpen ? 'chat-open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Chat with Lawra AI"
      >
        <div className="launcher-icon">
          <MessageSquare size={24} strokeWidth={2} />
        </div>
        <div className="launcher-pulse"></div>
      </button>

      {/* Chat Window - Futuristic Glass Panel */}
      {isOpen && (
        <div 
          className="lawra-chat-window"
          ref={chatWindowRef}
        >
          {/* Header */}
          <div className="lawra-chat-header">
            <div className="header-brand">
              <img src="/lawra-avatar.png" alt="Lawra" className="chat-avatar" />
              <div className="brand-text">
                <span className="brand-name">Lawra AI</span>
                <div className="status-row">
                  <div className={`status-indicator ${aiStatus}`}></div>
                  <span className="status-text">{aiStatus === 'thinking' ? 'Thinking...' : 'Online'}</span>
                </div>
              </div>
            </div>
            <div className="header-controls">
              <button 
                className="control-btn"
                onClick={() => setIsOpen(false)}
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="lawra-chat-messages">
                <HorizontalMatrixBackground />
                {messages.map((message, index) => (
                  <div 
                    key={index} 
                    className={`lawra-message ${message.role}`}
                  >
                    <div className="message-bubble">
                      <div className="message-meta">
                        <span className="message-author">
                          {message.role === 'user' ? 'You' : 'Lawra'}
                        </span>
                      </div>
                      <div 
                        className="message-text"
                        dangerouslySetInnerHTML={{ __html: cleanHtml(message.content) }}
                      />
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="lawra-message assistant">
                    <div className="message-bubble">
                      <div className="message-meta">
                        <span className="message-author">Lawra</span>
                      </div>
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Composer Area */}
              <div className="lawra-chat-composer">
                <div className="composer-inner">
                  <Textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask Lawra anything..."
                    rows={1}
                    className="lawra-input"
                    disabled={isLoading}
                  />
                  <button
                    onClick={handleSend}
                    disabled={!input.trim() || isLoading}
                    className="lawra-send-btn"
                    aria-label="Send message"
                  >
                    <Send size={18} />
                  </button>
                </div>
              </div>
        </div>
      )}
    </>
  );
};

export default FloatingChatWidget;
